import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Card, Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Flex from "components/common/Flex";
import ApexCharts from "react-apexcharts";
import { useAppContext } from "providers/AppProvider";
import { useSelector } from "react-redux";

const defaultRadius = ["50%", "70%"];
const defaultRate = "0%";


const SummaryStatCard = ({ title, value, color, bgColor }) => (
  <Col xs={12} sm={6} md={3} className="mb-3 me-3">
    <div
      className="p-3 rounded-4 border-0  w-100 d-flex flex-column justify-content-center shadow-none"
      style={{
        backgroundColor: bgColor,
        minHeight: "90px",
        border: `1px solid ${color}20`,
      }}
    >
      <div className="fw-bold mb-1 fs-11 text-uppercase ls-1" style={{ color }}>
        {title}
      </div>
      <div className="h2 mb-0 fw-bold" style={{ color }}>
        {Number(value || 0).toLocaleString()}
      </div>
    </div>
  </Col>
);

// Main Component
const TotalRatePreauthorization = () => {
  const { generalDashboardData } = useSelector((state) => state.dashboard);
  const { config: { isDark } } = useAppContext();

  // Extract preauth data or use defaults
  const preauth = generalDashboardData?.preauth || {
    total: 0,
    pended: 0,
    approved: 0,
    rejected: 0,
    noresp: 0,
    partial: 0,
    error: 0,
    prepared: 0,
    cancel: 0,
    queue: 0,
    adjudication: 0,
  };

  // Group statuses
  const pendedQueued = (preauth.pended || 0) + (preauth.queue || 0);

  const chartData = useMemo(() => [
    { name: "Approved", value: preauth.approved || 0, color: "#10b981" },
    { name: "Rejected", value: preauth.rejected || 0, color: "#ef4444" },
    { name: "Cancelled", value: preauth.cancel || 0, color: "#64748b" },
    { name: "Not Required", value: preauth?.["not-required"] || 0, color: "#8b5cf6" },
    { name: "Pended/Queued", value: pendedQueued, color: "#f59e0b" },
    { name: "Partially Approved", value: preauth.partial || 0, color: "#06b6d4" },
    { name: "No Response", value: preauth.noresp || 0, color: "#94a3b8" }
  ], [preauth, pendedQueued]);

  const approvalsCount = (preauth.approved || 0);
  const rejectionsCount = preauth.rejected || 0;

  const chartOptions = useMemo(() => {
    const bgColor = isDark ? '#0b1727' : '#F2F6FC';
    const borderColor = isDark ? 'rgba(255, 255, 255, 0.15)' : '#edf2f9';
    const textColor = isDark ? '#f8fafc' : '#4d5969';

    return {
      series: [{
        name: 'Count',
        data: chartData.map(d => d.value)
      }],
      options: {
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: false },
          fontFamily: 'Inter, sans-serif'
        },
        colors: chartData.map(d => d.color),
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: '40%',
            distributed: true,
            dataLabels: { position: 'top' }
          }
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        grid: {
          show: true,
          borderColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f1f5f9',
          strokeDashArray: 4,
          padding: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 10
          }
        },
        xaxis: {
          categories: chartData.map(d => d.name),
          labels: {
            rotate: 0,
            style: {
              colors: isDark ? '#94a3b8' : '#475569',
              fontSize: '11px',
              fontWeight: 600,
              fontFamily: 'inherit'
            },
            formatter: function (val) {
              if (val.length > 12) {
                const mid = Math.ceil(val.length / 2);
                const spaceIndex = val.indexOf(" ", mid - 4);
                if (spaceIndex > 0) return [val.slice(0, spaceIndex), val.slice(spaceIndex + 1)];
              }
              return val;
            }
          },
          axisBorder: { show: false },
          axisTicks: { show: false }
        },
        yaxis: {
          labels: {
            style: {
              colors: isDark ? '#64748b' : '#64748b',
              fontSize: '11px',
              fontWeight: 500
            },
            formatter: (val) => val.toFixed(0)
          }
        },
        tooltip: {
          enabled: true,
          shared: false,
          intersect: true,
          custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            const data = w.config.series[seriesIndex].data[dataPointIndex];
            const category = w.config.xaxis.categories[dataPointIndex];
            const color = w.config.colors[dataPointIndex];
            return `
              <div class="px-3 py-2 rounded-2 shadow-sm" style="background-color: ${bgColor}; border: 1px solid ${borderColor}; min-width: 140px;">
                <div class="fw-bold mb-1 fs-12" style="color: ${textColor}">${category}</div>
                <div class="fs-11" style="color: ${isDark ? '#94a3b8' : '#64748b'}">count : <span class="fw-bold" style="color: ${color}">${data.toLocaleString()}</span></div>
              </div>
             `;
          }
        }
      }
    };
  }, [chartData, isDark]);

  return (
    <Col xs={12} md={6} xl={8} className="mb-4">
      <Card className="h-100 border-0 shadow-sm rounded-4">
        <Card.Body className="p-4">
          <Flex justifyContent="between" alignItems="start" className="mb-4">
            <div>
              <h5 className="mb-1 fw-bold fs-9 text-900">Pre-Authorization Lifecycle</h5>
              <p className="mb-0 text-500 fs-11">
                Comparative analysis of submission outcomes from NPHIES gateway
              </p>
            </div>
            <div
              className="rounded-3 d-flex bg-info align-items-center justify-content-center"
              style={{ width: '32px', height: '32px' }}
            >
              <FontAwesomeIcon icon="chart-line" className="text-dark fs-11" />
            </div>
          </Flex>

          <div className="mb-4">
            <ApexCharts
              options={chartOptions.options}
              series={chartOptions.series}
              type="bar"
              height={300}
            />
          </div>

          <Flex gap={3} className="flex-wrap">
            <SummaryStatCard
              title="Approvals"
              value={approvalsCount}
              color="#065f46"
              bgColor="#ecfdf5"
            />
            <SummaryStatCard
              title="Rejections"
              value={rejectionsCount}
              color="#991b1b"
              bgColor="#fef2f2"
            />
          </Flex>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default TotalRatePreauthorization;
